// Dashboard.js — Student profile + placement stats overview
import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Navbar from './Navbar';
import API   from '../api';

export default function Dashboard() {
  const navigate = useNavigate();
  const student  = JSON.parse(localStorage.getItem('student')) || {};

  const [eligibleCount, setEligibleCount]   = useState(null);
  const [appliedCount,  setAppliedCount]    = useState(null);
  const [totalCompanies, setTotalCompanies] = useState(null);
  const [loading, setLoading]               = useState(true);

  useEffect(() => {
    if (!student.id) return;

    const fetchStats = async () => {
      try {
        const [eligRes, appRes, compRes] = await Promise.all([
          API.get(`/eligible_companies/${student.id}`),
          API.get(`/applications/${student.id}`),
          API.get('/companies'),
        ]);
        setEligibleCount(eligRes.data.filter(c => c.eligible).length);
        setAppliedCount(appRes.data.length);
        setTotalCompanies(compRes.data.length);
      } catch { /* silently ignore — API might not be running yet */ }
      finally { setLoading(false); }
    };

    fetchStats();
  }, [student.id]);

  // Derive skills array
  const skills = (student.skills || '')
    .split(',')
    .map(s => s.trim())
    .filter(Boolean);

  // CGPA color helper
  const cgpaColor = () => {
    if (student.cgpa >= 8)  return 'var(--accent-green)';
    if (student.cgpa >= 6.5) return 'var(--accent-blue)';
    return 'var(--accent-orange)';
  };

  return (
    <>
      <Navbar />
      <div className="container" style={{ paddingTop: '2rem', paddingBottom: '3rem' }}>

        {/* Page Header */}
        <div className="page-header">
          <h2>Dashboard</h2>
          <p>Your placement overview and profile summary</p>
        </div>

        {/* Stats Bar */}
        <div className="stats-bar">
          <div className="stats-bar-item">
            <div className="stats-bar-value blue">{loading ? '—' : totalCompanies}</div>
            <div className="stats-bar-label">Total Companies</div>
          </div>
          <div className="stats-bar-item">
            <div className="stats-bar-value green">{loading ? '—' : eligibleCount}</div>
            <div className="stats-bar-label">Eligible For</div>
          </div>
          <div className="stats-bar-item">
            <div className="stats-bar-value orange">{loading ? '—' : appliedCount}</div>
            <div className="stats-bar-label">Applied To</div>
          </div>
          <div className="stats-bar-item">
            <div className="stats-bar-value" style={{ color: cgpaColor() }}>
              {student.cgpa?.toFixed(2) || '—'}
            </div>
            <div className="stats-bar-label">Your CGPA</div>
          </div>
        </div>

        {/* Main Grid */}
        <div className="dashboard-grid">

          {/* ── Left: Profile Card ── */}
          <div>
            <div className="card profile-card">
              <div className="profile-avatar">
                {(student.name || 'U')[0].toUpperCase()}
              </div>
              <div className="profile-name">{student.name}</div>
              <div className="profile-dept">{student.department} Department</div>

              {/* Stats grid */}
              <div className="profile-stats">
                <div className="stat-item">
                  <div className="stat-value" style={{ color: cgpaColor() }}>
                    {student.cgpa?.toFixed(2)}
                  </div>
                  <div className="stat-label">CGPA</div>
                </div>
                <div className="stat-item">
                  <div className="stat-value" style={{ color: student.arrears === 0 ? 'var(--accent-green)' : 'var(--accent-red)' }}>
                    {student.arrears}
                  </div>
                  <div className="stat-label">Arrears</div>
                </div>
              </div>

              <div className="section-heading">Contact</div>
              <p style={{ fontSize: '0.82rem', color: 'var(--text-muted)', wordBreak: 'break-word' }}>
                📧 {student.email}
              </p>

              {skills.length > 0 && (
                <>
                  <div className="section-heading">Skills</div>
                  <div className="skills-list">
                    {skills.map(s => (
                      <span key={s} className="skill-tag">{s}</span>
                    ))}
                  </div>
                </>
              )}

              <button
                className="btn btn-outline btn-full"
                style={{ marginTop: '1.5rem' }}
                onClick={() => navigate('/companies')}
              >
                🏢 View Companies
              </button>
              <button
                className="btn btn-primary btn-full"
                style={{ marginTop: '0.5rem' }}
                onClick={() => navigate('/applications')}
              >
                📄 My Applications
              </button>
            </div>
          </div>

          {/* ── Right: Quick Info ── */}
          <div>
            {/* Eligibility Summary */}
            <div className="card" style={{ marginBottom: '1rem' }}>
              <h3 style={{ fontSize: '1.05rem', fontWeight: 700, marginBottom: '1rem' }}>
                🎯 Placement Eligibility Summary
              </h3>
              <p style={{ color: 'var(--text-muted)', fontSize: '0.9rem', marginBottom: '1rem' }}>
                Eligibility is automatically calculated based on your CGPA, arrears, and department against each company's criteria.
              </p>
              <div style={{ display: 'flex', gap: '0.75rem', flexWrap: 'wrap' }}>
                <div style={{
                  flex: 1, minWidth: 120,
                  background: 'rgba(46,204,113,0.08)',
                  border: '1px solid rgba(46,204,113,0.2)',
                  borderRadius: 'var(--radius-sm)',
                  padding: '1rem', textAlign: 'center'
                }}>
                  <div style={{ fontSize: '1.5rem', fontWeight: 800, color: 'var(--accent-green)' }}>
                    {loading ? '…' : eligibleCount}
                  </div>
                  <div style={{ fontSize: '0.75rem', color: 'var(--text-muted)', marginTop: 4 }}>Companies Eligible</div>
                </div>
                <div style={{
                  flex: 1, minWidth: 120,
                  background: 'rgba(231,76,60,0.08)',
                  border: '1px solid rgba(231,76,60,0.2)',
                  borderRadius: 'var(--radius-sm)',
                  padding: '1rem', textAlign: 'center'
                }}>
                  <div style={{ fontSize: '1.5rem', fontWeight: 800, color: 'var(--accent-red)' }}>
                    {loading ? '…' : (totalCompanies !== null && eligibleCount !== null ? totalCompanies - eligibleCount : '…')}
                  </div>
                  <div style={{ fontSize: '0.75rem', color: 'var(--text-muted)', marginTop: 4 }}>Not Eligible</div>
                </div>
                <div style={{
                  flex: 1, minWidth: 120,
                  background: 'rgba(243,156,18,0.08)',
                  border: '1px solid rgba(243,156,18,0.2)',
                  borderRadius: 'var(--radius-sm)',
                  padding: '1rem', textAlign: 'center'
                }}>
                  <div style={{ fontSize: '1.5rem', fontWeight: 800, color: 'var(--accent-orange)' }}>
                    {loading ? '…' : appliedCount}
                  </div>
                  <div style={{ fontSize: '0.75rem', color: 'var(--text-muted)', marginTop: 4 }}>Applied</div>
                </div>
              </div>
            </div>

            {/* Criteria explanation */}
            <div className="card">
              <h3 style={{ fontSize: '1.05rem', fontWeight: 700, marginBottom: '1rem' }}>
                📋 Eligibility Criteria
              </h3>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
                {[
                  { icon: '📊', label: 'CGPA Check',       desc: `Your CGPA (${student.cgpa}) must be ≥ company's minimum CGPA`, ok: true },
                  { icon: '❌', label: 'Arrears Check',     desc: `Your active arrears (${student.arrears}) must be ≤ company's allowed maximum`, ok: student.arrears === 0 },
                  { icon: '🏛️', label: 'Department Match',  desc: `Your department (${student.department}) must match the company's target`, ok: true },
                ].map(item => (
                  <div key={item.label} style={{
                    display: 'flex', gap: '0.75rem',
                    background: 'rgba(255,255,255,0.03)',
                    border: '1px solid var(--border)',
                    borderRadius: 'var(--radius-sm)',
                    padding: '0.85rem',
                    alignItems: 'flex-start'
                  }}>
                    <span style={{ fontSize: '1.2rem' }}>{item.icon}</span>
                    <div>
                      <div style={{ fontWeight: 600, fontSize: '0.875rem' }}>{item.label}</div>
                      <div style={{ fontSize: '0.8rem', color: 'var(--text-muted)', marginTop: '0.2rem' }}>{item.desc}</div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>

        </div>
      </div>
    </>
  );
}
